// ./src/components/features/Artists/TouringPartyDrawer/TouringPartyDrawer.tsx
//
// Manage the touring party / entourage of one booked act. Opened from the
// EventArtistBookingDrawer for a saved booking. The people travelling with an act
// are event guests (EventGuest) linked to the booking via `eventArtistId`, with
// `guestRole` = performer | crew | entourage. Reuses EventGuestService (list /
// create / update / remove) and the shared PersonPicker (link an existing contact
// or create one in place). Changes save immediately and call onChanged so the
// caller can refresh (e.g. a headcount on the booking). Mirrors BudgetPaymentsDrawer.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Group, Text, Card, ActionIcon, Tooltip, Loader, Center, Badge, Divider } from "@mantine/core";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  EventGuest,
  EventGuestService,
  EVENT_GUEST_ROLE,
  getGuestDisplayName,
  Person,
  getPersonDisplayName,
  listAllPages,
} from "@/utils/api";
import { useToast } from "@/hooks";
import { AppDrawer, AppIcon, EmptyState, ConfirmModal, SelectField } from "@/components/generic";
import { PersonPicker } from "@/components/features/EventGuests/PersonPicker";
import { refusalReason } from "@/utils";

export interface TouringPartyDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** The booked act (EventArtist) whose party this is. */
  eventArtistId: string;
  /** The act's name, for the drawer title. */
  actName?: string | null;
  /** Fires after any add / role-change / remove, so the caller can refresh. */
  onChanged?: () => void;
}

// The only roles offered for a touring-party member.
const PARTY_ROLE_OPTIONS = [
  { value: EVENT_GUEST_ROLE.PERFORMER, label: "Performer" },
  { value: EVENT_GUEST_ROLE.CREW, label: "Crew" },
  { value: EVENT_GUEST_ROLE.ENTOURAGE, label: "Entourage" },
];

export const TouringPartyDrawer: React.FC<TouringPartyDrawerProps> = ({
  opened,
  onClose,
  eventId,
  eventArtistId,
  actName,
  onChanged,
}) => {
  const toast = useToast();

  const [members, setMembers] = useState<EventGuest[]>([]);
  /**
   * ⚠ Whether the FIRST read for this target has COME BACK — not the same as "not loading". The
   * read starts in an effect, which runs after the first paint, so branching on
   * `loading && rows.length === 0` shows the empty state for one frame every time the drawer opens.
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<EventGuest | null>(null);
  const [pendingAdd, setPendingAdd] = useState<{ person: Person; existingGuest: EventGuest } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!eventArtistId) {
      return;
    }

    const result = await listAllPages(
      EventGuestService.list,
      { eventId: [eventId], eventArtistId: [eventArtistId] },
      [{ name: "lastName", order: "ASC" }, { name: "firstName", order: "ASC" }]
    );
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setMembers(result.data as EventGuest[]);
    } else {
      toast.error(refusalReason(result) || "Could not load the party.");
    }
  }, [eventId, eventArtistId, toast]);

  useEffect(() => {
    if (opened && eventArtistId) {
      setMembers([]);
      setIsLoaded(false);
      load();
    }
  }, [opened, eventArtistId, load]);

  const afterChange = useCallback(async () => {
    await load();
    onChanged?.();
  }, [load, onChanged]);

  // Contact ids already in the party — greyed out in the picker so a person isn't added twice.
  const excludeIds = useMemo(
    () => members.map((m) => m.personId).filter((id): id is string => Boolean(id)),
    [members]
  );

  // Actually link the person into this act's party (server promotes an existing
  // guest row if the person is already on the event, else inserts a fresh one).
  const doAdd = useCallback(
    async (person: Person) => {
      setAdding(true);
      const result = await EventGuestService.create({
        eventId,
        eventArtistId,
        personId: person.id,
        guestRole: EVENT_GUEST_ROLE.ENTOURAGE,
      });
      setAdding(false);

      if (result.didSucceed()) {
        afterChange();
      } else {
        toast.error(refusalReason(result) || `Could not add ${getPersonDisplayName(person)}.`);
      }
    },
    [eventId, eventArtistId, afterChange, toast]
  );

  const addPerson = useCallback(
    async (person: Person | null) => {
      if (!person?.id) {
        return;
      }

      // Is this contact already an event guest? If so, adding them to the party
      // MOVES their existing guest row (role → Entourage, linked to this act) —
      // confirm first so it's a deliberate switch, not a silent surprise.
      setAdding(true);
      const existing = await EventGuestService.list(
        { eventId: [eventId], personId: [person.id] },
        { page: 1, pageSize: 5 }
      );
      setAdding(false);

      // The list already excludes soft-removed rows, so any returned row is a live guest.
      const rows = existing.didSucceed() && Array.isArray(existing.data) ? (existing.data as EventGuest[]) : [];
      const already = rows.find((g) => !!g?.id) || null;

      if (already) {
        // Already in THIS act's party (shouldn't happen — excluded in the picker).
        if (already.eventArtistId === eventArtistId) {
          return;
        }

        setPendingAdd({ person, existingGuest: already });

        return;
      }

      await doAdd(person);
    },
    [eventId, eventArtistId, doAdd]
  );

  const confirmAdd = useCallback(async () => {
    const p = pendingAdd?.person ?? null;
    setPendingAdd(null);

    if (p) {
      await doAdd(p);
    }
  }, [pendingAdd, doAdd]);

  const changeRole = useCallback(
    async (member: EventGuest, role: string | null) => {
      if (!member.id || !role || role === member.guestRole) {
        return;
      }

      setSavingRoleId(member.id);
      const result = await EventGuestService.update({ id: member.id }, { guestRole: role });
      setSavingRoleId(null);

      if (result.didSucceed()) {
        afterChange();
      } else {
        toast.error(refusalReason(result) || "Could not change the role.");
      }
    },
    [afterChange, toast]
  );

  const confirmRemove = useCallback(async () => {
    const target = removeTarget;
    setRemoveTarget(null);

    if (!target?.id) {
      return;
    }

    setRemovingId(target.id);
    const result = await EventGuestService.remove(target.id);
    setRemovingId(null);

    if (result.didSucceed()) {
      afterChange();
    } else {
      toast.error(refusalReason(result) || "Could not remove the member.");
    }
  }, [removeTarget, afterChange, toast]);

  const headcount = members.length;

  const memberRow = (m: EventGuest) => (
    <Card key={m.id} withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" gap="sm" align="center">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <AppIcon iconName="User" size={18} />
          <Text fw={600} size="md" truncate>
            {getGuestDisplayName(m)}
          </Text>
        </Group>

        <Group gap="xs" wrap="nowrap">
          <SelectField
            data={PARTY_ROLE_OPTIONS}
            value={m.guestRole || EVENT_GUEST_ROLE.ENTOURAGE}
            onChange={(_name, v) => changeRole(m, v)}
            allowDeselect={false}
            disabled={savingRoleId === m.id}
            rightSection={savingRoleId === m.id ? <Loader size="xs" /> : undefined}
            w={150}
            size="md"
            aria-label="Party role"
          />
          <Tooltip label="Remove" withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => setRemoveTarget(m)}
              loading={removingId === m.id}
              aria-label="Remove"
            >
              <AppIcon iconName="Trash" size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Card>
  );

  const body = () => {
    if (!isLoaded) {
      return (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      );
    }

    return (
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Everyone travelling with the act — performing members, hands-on crew, and guests.
          </Text>
          <Badge variant="light" size="lg" leftSection={<AppIcon iconName="Users" size={14} />}>
            {headcount} named
          </Badge>
        </Group>

        {members.length === 0 ? (
          <EmptyState
            iconName="Users"
            title="No party members yet"
            description="Add the crew, entourage, and performing members travelling with this act."
          />
        ) : (
          <Stack gap="xs">{members.filter((m) => m.id).map((m) => memberRow(m))}</Stack>
        )}

        <Divider label="Add a member" labelPosition="left" mt="xs" />

        <PersonPicker
          value={null}
          onSelect={addPerson}
          excludeIds={excludeIds}
          onCreateOpenChange={setCreateOpen}
          label="Link a contact"
          description="Pick an existing contact or create a new one. New members join as Entourage — change their role above."
          placeholder={adding ? "Adding…" : "Search contacts…"}
          disabled={adding}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={actName ? `Party — ${actName}` : "Touring party"}
        size="md"
        actionType="close"
        cancelLabel="Close"
        closable={!createOpen}
      >
        {body()}
      </AppDrawer>

      <ConfirmModal
        opened={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        type="danger"
        title="Remove this member?"
        message={removeTarget ? `${getGuestDisplayName(removeTarget)} will be removed from the act's party.` : ""}
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        opened={!!pendingAdd}
        onClose={() => setPendingAdd(null)}
        onConfirm={confirmAdd}
        type="warning"
        title="Already a guest of this event"
        message={
          pendingAdd
            ? `${getPersonDisplayName(pendingAdd.person)} is already ${
                pendingAdd.existingGuest.eventArtistId ? "in another act's party" : "a guest of this event"
              }. Add them to ${actName || "this act"}'s party? Their role will change to Entourage.`
            : ""
        }
        confirmLabel="Add to party"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default TouringPartyDrawer;
